import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { 
  LayoutDashboard, 
  Users, 
  UserCircle, 
  Contact, 
  Settings, 
  LogOut,
  Sun,
  Moon,
  ChevronLeft,
  ChevronRight,
  Brain,
  Building,
  FolderKanban,
  MessagesSquare,
  Network,
  Activity,
  ClipboardList,
  Inbox
} from 'lucide-react';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const allNavItems = [
  { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', roles: ['owner', 'admin', 'user'] },
  { path: '/brain', icon: Brain, label: 'Cerveau', roles: ['owner', 'admin'] },
  { path: '/activity', icon: Activity, label: 'Activité', roles: ['owner', 'admin'] },
  { path: '/suivi', icon: ClipboardList, label: 'Suivi', roles: ['owner', 'admin', 'user'] },
  { path: '/clones', icon: Brain, label: 'Clones', roles: ['owner', 'admin', 'user'] },
  { path: '/chat/global', icon: MessagesSquare, label: 'Chat Entreprise', roles: ['owner', 'admin', 'user'] },
  { path: '/dossiers', icon: FolderKanban, label: 'Dossiers', roles: ['owner', 'admin', 'user'] },
  { path: '/organizations', icon: Building, label: 'Organisations', roles: ['owner', 'admin'] },
  { path: '/contacts', icon: Contact, label: 'Contacts', roles: ['owner', 'admin', 'user'] },
  { path: '/suggestions', icon: Inbox, label: 'Validations', roles: ['owner', 'admin', 'user'], badgeKey: 'pending_total' },
  { path: '/users', icon: Users, label: 'Team', roles: ['owner', 'admin'] },
  { path: '/departments', icon: Network, label: 'Departements', roles: ['owner', 'admin'] },
  { path: '/settings', icon: Settings, label: 'Settings', roles: ['owner'] },
];

export const Sidebar = () => {
  const { user, tenant, token, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  // Poll the pending-suggestions counter every 60s so the badge stays fresh.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const fetchCounts = async () => {
      try {
        const r = await axios.get(`${API}/suggestions/counts`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setPendingCount(r.data?.pending_total || 0);
      } catch (_) { /* silent — badge just stays empty */ }
    };
    fetchCounts();
    const id = setInterval(fetchCounts, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [token]);

  const navItems = allNavItems.filter(item => {
    if (!item.roles.includes(user?.role || 'user')) return false;
    if (item.feature && !tenant?.features?.[item.feature]) return false;
    return true;
  });

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <aside 
      className={cn(
        "h-screen sticky top-0 left-0 z-40 flex flex-col border-r border-border bg-card/50 backdrop-blur-xl transition-all duration-300",
        collapsed ? "w-16" : "w-64"
      )}
      data-testid="sidebar"
    >
      {/* Logo */}
      <div className="flex items-center justify-between h-16 px-4 border-b border-border">
        {!collapsed && (
          <div className="flex items-center gap-2">
            <img src="/cloneia-logo.png" alt="Clone IA" className="w-8 h-8 rounded-lg object-cover" />
            <span className="font-heading font-bold text-lg tracking-tight">Clone IA</span>
          </div>
        )}
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={() => setCollapsed(!collapsed)}
          className="h-8 w-8"
          data-testid="sidebar-toggle"
        >
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </Button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 px-2 space-y-1">
        {navItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            className={({ isActive }) => cn(
              "flex items-center gap-3 px-3 py-2.5 rounded-lg font-medium text-sm transition-colors duration-200",
              isActive 
                ? "bg-primary text-primary-foreground" 
                : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
            )}
            data-testid={`nav-${item.path.slice(1)}`}
          >
            <item.icon className="w-5 h-5 flex-shrink-0" />
            {!collapsed && <span className="flex-1">{item.label}</span>}
            {item.badgeKey === 'pending_total' && pendingCount > 0 && (
              <span
                className={cn(
                  "ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-semibold",
                  "bg-amber-500 text-white",
                  collapsed && "absolute top-2 right-2 min-w-[18px] h-[18px]"
                )}
                data-testid="nav-suggestions-badge"
              >
                {pendingCount > 99 ? '99+' : pendingCount}
              </span>
            )}
          </NavLink>
        ))}
      </nav>

      {/* Footer */}
      <div className="p-4 border-t border-border space-y-3">
        {/* Theme Toggle */}
        <Button 
          variant="ghost" 
          size="sm"
          onClick={toggleTheme}
          className={cn("w-full justify-start gap-3", collapsed && "justify-center px-0")}
          data-testid="theme-toggle"
        >
          {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          {!collapsed && <span>{theme === 'dark' ? 'Light Mode' : 'Dark Mode'}</span>}
        </Button>

        {/* User Info */}
        {!collapsed && (
          <div className="px-2 py-2">
            <p className="font-medium text-sm truncate">{user?.first_name} {user?.last_name}</p>
            <p className="text-xs text-muted-foreground truncate">{tenant?.name}</p>
          </div>
        )}

        {/* Logout */}
        <Button 
          variant="ghost" 
          size="sm"
          onClick={handleLogout}
          className={cn("w-full justify-start gap-3 text-destructive hover:text-destructive hover:bg-destructive/10", collapsed && "justify-center px-0")}
          data-testid="logout-btn"
        >
          <LogOut className="w-4 h-4" />
          {!collapsed && <span>Logout</span>}
        </Button>
      </div>
    </aside>
  );
};
